import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DollarSign, Save, ClipboardPaste, ShieldCheck, Search, CheckCircle2, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DIV_LABEL = { field: 'Field', shop: 'Shop', office: 'Office' };

export default function PayRates() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  const [edits, setEdits] = useState({}); // employee_id -> string
  const [saving, setSaving] = useState(false);
  const [burden, setBurden] = useState('');
  const [defaultWage, setDefaultWage] = useState('');
  const [marginTarget, setMarginTarget] = useState('');
  const [marginWarn, setMarginWarn] = useState('');
  const [pasteText, setPasteText] = useState('');
  const [preview, setPreview] = useState(null);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/pay-rates`);
      setData(r.data);
      setBurden(String(r.data.settings.burden_per_hour));
      setDefaultWage(String(r.data.settings.default_wage));
      setMarginTarget(String(r.data.settings.margin_target_pct ?? 35));
      setMarginWarn(String(r.data.settings.margin_warn_pct ?? 20));
      setEdits({});
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load pay rates'); }
  };
  useEffect(() => { load(); }, []);

  const rows = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    return data.employees.filter((e) => !needle || e.name.toLowerCase().includes(needle));
  }, [data, q]);

  const dirty = Object.keys(edits).filter((id) => {
    const emp = data?.employees.find((e) => e.employee_id === id);
    const cur = emp?.rate == null ? '' : String(emp.rate);
    return (edits[id] ?? '') !== cur;
  });

  const saveAll = async () => {
    if (!dirty.length) return;
    setSaving(true);
    let ok = 0;
    for (const id of dirty) {
      try { await axios.put(`${API}/pay-rates/${id}`, { rate: edits[id] === '' ? null : parseFloat(edits[id]) }); ok += 1; }
      catch (e) { toast.error(`${data.employees.find((x) => x.employee_id === id)?.name}: ${e.response?.data?.detail || 'failed'}`); }
    }
    setSaving(false);
    if (ok) toast.success(`${ok} rate${ok > 1 ? 's' : ''} saved`);
    load();
  };

  const saveSettings = async () => {
    try {
      const r = await axios.put(`${API}/pay-rates/settings`, { burden_per_hour: parseFloat(burden), default_wage: parseFloat(defaultWage), margin_target_pct: parseFloat(marginTarget), margin_warn_pct: parseFloat(marginWarn) });
      setData((d) => ({ ...d, settings: r.data }));
      toast.success('Burden / default / margin targets saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
  };

  const runImport = async (apply) => {
    if (!pasteText.trim()) return;
    try {
      const r = await axios.post(`${API}/pay-rates/import`, { text: pasteText, apply });
      setPreview(r.data);
      if (apply) { toast.success(`${r.data.matched.length} rate${r.data.matched.length !== 1 ? 's' : ''} loaded`); setPasteText(''); setPreview(null); load(); }
    } catch (e) { toast.error(e.response?.data?.detail || 'Import failed'); }
  };

  if (!data) return <div className="p-8 text-slate-500">Loading…</div>;
  const missing = data.employees.filter((e) => e.rate == null && e.division !== 'office').length;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5" data-testid="pay-rates-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><DollarSign className="w-6 h-6 text-emerald-600" /> Pay Rates</h1>
          <p className="text-sm text-slate-600 mt-1">Hourly wage per person — drives the <b>actual labour cost</b> on every project (OT 1.5× on hours over 8 a day + weekends, travel at straight time, plus burden on every hour).</p>
          <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> Super-admins only. PMs see the resulting Labour $ on the project, never the rates.</p>
        </div>
        <div className="flex items-end gap-3 bg-white rounded-xl border border-slate-200 p-3 shadow-sm">
          <label className="text-xs text-slate-600">Burden $/hr
            <input type="number" step="0.5" value={burden} onChange={(e) => setBurden(e.target.value)} data-testid="pay-burden-input"
              className="block w-24 mt-1 px-2 py-1.5 border border-slate-300 rounded-lg text-sm text-slate-900" />
          </label>
          <label className="text-xs text-slate-600" title="Used for anyone with no rate set — they are flagged on the project">Default wage $/hr
            <input type="number" step="0.5" value={defaultWage} onChange={(e) => setDefaultWage(e.target.value)} data-testid="pay-default-input"
              className="block w-24 mt-1 px-2 py-1.5 border border-slate-300 rounded-lg text-sm text-slate-900" />
          </label>
          <label className="text-xs text-slate-600" title="Leaderboard margin pill turns green at or above this">Margin target %
            <input type="number" step="1" value={marginTarget} onChange={(e) => setMarginTarget(e.target.value)} data-testid="pay-margin-target-input"
              className="block w-20 mt-1 px-2 py-1.5 border border-emerald-300 rounded-lg text-sm text-slate-900" />
          </label>
          <label className="text-xs text-slate-600" title="Amber between this and the target; red below">Margin warn %
            <input type="number" step="1" value={marginWarn} onChange={(e) => setMarginWarn(e.target.value)} data-testid="pay-margin-warn-input"
              className="block w-20 mt-1 px-2 py-1.5 border border-amber-300 rounded-lg text-sm text-slate-900" />
          </label>
          <button onClick={saveSettings} className="px-3 py-1.5 bg-white hover:bg-slate-50 text-slate-900 rounded-lg text-sm" data-testid="pay-settings-save">Save</button>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_360px]">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-slate-200">
            <div className="relative">
              <Search className="w-4 h-4 text-slate-500 absolute left-2.5 top-2.5" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a name" data-testid="pay-search"
                className="pl-8 pr-3 py-2 border border-slate-300 rounded-lg text-sm w-56 text-slate-900" />
            </div>
            <div className="flex items-center gap-3 text-xs text-slate-600">
              <span data-testid="pay-set-count"><b>{data.set_count}</b> of {data.employees.length} set</span>
              {missing > 0 && <span className="text-amber-700 inline-flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> {missing} shop/field with no rate</span>}
              <button onClick={saveAll} disabled={!dirty.length || saving} data-testid="pay-save-all"
                className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white rounded-lg text-sm font-medium inline-flex items-center gap-1">
                <Save className="w-4 h-4" /> {saving ? 'Saving…' : `Save ${dirty.length || ''}`}
              </button>
            </div>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
              <tr><th className="px-4 py-2">Employee</th><th className="px-4 py-2">Role</th><th className="px-4 py-2">Side</th><th className="px-4 py-2 text-right">Wage $/hr</th><th className="px-4 py-2">Updated</th></tr>
            </thead>
            <tbody>
              {rows.map((e) => {
                const val = edits[e.employee_id] ?? (e.rate == null ? '' : String(e.rate));
                const changed = dirty.includes(e.employee_id);
                return (
                  <tr key={e.employee_id} className={`border-t border-slate-100 ${changed ? 'bg-amber-50' : ''}`} data-testid={`pay-row-${e.employee_id}`}>
                    <td className="px-4 py-2 font-medium text-slate-900">{e.name}</td>
                    <td className="px-4 py-2 text-slate-600 capitalize">{(e.role || '').replace(/_/g, ' ')}</td>
                    <td className="px-4 py-2"><span className={`text-[11px] px-2 py-0.5 rounded-full ${e.division === 'field' ? 'bg-blue-100 text-blue-800' : e.division === 'shop' ? 'bg-orange-100 text-orange-800' : 'bg-slate-100 text-slate-600'}`}>{DIV_LABEL[e.division] || e.division}</span></td>
                    <td className="px-4 py-2 text-right">
                      <input type="number" step="0.25" min="0" value={val} placeholder="—" data-testid={`pay-input-${e.employee_id}`}
                        onChange={(ev) => setEdits((x) => ({ ...x, [e.employee_id]: ev.target.value }))}
                        className={`w-24 px-2 py-1 text-right border rounded-lg text-slate-900 ${e.rate == null && !val ? 'border-amber-300 bg-amber-50/40' : 'border-slate-300'}`} />
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-500">{e.updated_at ? `${e.updated_at.slice(0, 10)}${e.updated_by ? ` · ${e.updated_by}` : ''}` : ''}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 space-y-3 self-start">
          <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2"><ClipboardPaste className="w-4 h-4 text-slate-500" /> Paste a rate list</h2>
          <p className="text-xs text-slate-600">One person per line: <code className="bg-slate-100 px-1 rounded">Jordan Baker — $38.50</code>, <code className="bg-slate-100 px-1 rounded">Baker, Jordan 38.5</code> or a CSV column pair. Preview first, then load.</p>
          <textarea value={pasteText} onChange={(e) => { setPasteText(e.target.value); setPreview(null); }} rows={8} data-testid="pay-paste"
            placeholder={'Jordan Baker — $38.50\nMatt Vanderwiel — 39.25'} className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono text-slate-900" />
          <div className="flex gap-2">
            <button onClick={() => runImport(false)} disabled={!pasteText.trim()} className="px-3 py-1.5 bg-white hover:bg-slate-50 disabled:opacity-40 text-slate-900 rounded-lg text-sm" data-testid="pay-import-preview">Preview</button>
            <button onClick={() => runImport(true)} disabled={!preview || !preview.matched.length} className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white rounded-lg text-sm" data-testid="pay-import-apply">Load {preview?.matched.length || ''}</button>
          </div>
          {preview && (
            <div className="text-xs space-y-1" data-testid="pay-import-result">
              {preview.matched.map((m) => <div key={m.employee_id} className="flex items-center gap-1 text-emerald-800"><CheckCircle2 className="w-3.5 h-3.5" /> {m.name} → ${m.rate.toFixed(2)}{m.input !== m.name && <span className="text-slate-500">(from “{m.input}”)</span>}{m.also?.length > 0 && <span className="text-slate-500">· also {m.also.map((a) => a.name).join(', ')} (same person, second account)</span>}</div>)}
              {preview.unmatched.map((u, i) => <div key={i} className="flex items-center gap-1 text-amber-700"><AlertTriangle className="w-3.5 h-3.5" /> “{u.input}” (${u.rate.toFixed(2)}) — no matching employee account</div>)}
              {!preview.parsed && <div className="text-slate-500">Nothing I could read — each line needs a name and a number.</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
